import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { listProducts, listReadyMade } from "../lib/api";

/**
 * Sitewide search dropdown. Lives inside the Header.
 *
 * Searches across BOTH customizable products (by name, brand, sku) AND
 * ready-made designs (by name, category) using a client-side index that's
 * fetched once and cached in module state (so opening the popup is instant
 * after the first time).
 *
 * Behaviour:
 *  • Mobile: search icon button → expands to a full-width row of input below the header.
 *  • Desktop: input is visible inline in the header.
 *  • Typing >=2 chars filters the cached list and shows up to 8 results.
 *  • Enter on the input navigates to the first result.
 *  • Esc / outside-click closes the dropdown.
 */

// Module-level cache so the index loads once per session
let CACHE = { loaded: false, items: [] };

async function loadIndex() {
  if (CACHE.loaded) return CACHE.items;
  try {
    const [products, designs] = await Promise.all([
      listProducts().catch(() => []),
      listReadyMade().catch(() => []),
    ]);
    const items = [];
    for (const p of products) {
      items.push({
        kind: "product",
        id: p.id,
        title: p.name,
        sub: `${p.brand || ""}${p.sku ? " · " + p.sku : ""}`.trim() || "Customizable",
        price: p.base_price,
        image: p.image,
        href: `/customize/${p.slug || p.id}`,
        // iter55 — added description + tags so header search finds items
        // by keyword text (e.g. "moisture-wicking", "eco-friendly").
        haystack: [
          p.name, p.brand, p.sku, p.brand_line, p.category, p.model,
          p.description, ...(Array.isArray(p.tags) ? p.tags : []),
        ].filter(Boolean).join(" ").toLowerCase(),
      });
    }
    for (const d of designs) {
      items.push({
        kind: "design",
        id: d.id,
        title: d.name,
        sub: (d.category_slugs?.[0] || d.category || "Ready-Made").toString().toUpperCase().replace(/-/g, " "),
        price: d.base_price,
        // iter55 — ready-made products use image_front (not image).
        // Fallback chain: image_front → image → gallery[0].
        image: d.image_front || d.image || d.gallery?.[0],
        href: `/design/${d.slug || d.id}`,
        // iter55 — added tags + badges so header search finds designs
        // by keyword text (e.g. "trending", "bestseller", "anime").
        haystack: [
          d.name, d.description, d.category,
          ...(d.category_slugs || []),
          ...(Array.isArray(d.tags) ? d.tags : []),
          ...(Array.isArray(d.badges) ? d.badges : []),
        ].filter(Boolean).join(" ").toLowerCase(),
      });
    }
    CACHE = { loaded: true, items };
    return items;
  } catch {
    return [];
  }
}

export default function SearchBox({ variant = "desktop" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Lazy-load the index when the user first interacts with the search box
  const ensureIndex = async () => {
    if (CACHE.loaded) { setItems(CACHE.items); return; }
    setLoading(true);
    const data = await loadIndex();
    setItems(data);
    setLoading(false);
  };

  // Outside-click + Esc to close
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setMobileOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setMobileOpen(false); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Focus input when mobile expands
  useEffect(() => {
    if (mobileOpen) setTimeout(() => inputRef.current?.focus(), 50);
  }, [mobileOpen]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const hits = [];
    for (const it of items) {
      // Score: title-start > title-contains > haystack
      const t = it.title.toLowerCase();
      let score = 0;
      if (t.startsWith(q)) score = 3;
      else if (t.includes(q)) score = 2;
      else if (it.haystack.includes(q)) score = 1;
      if (score > 0) hits.push({ it, score });
    }
    hits.sort((a, b) => b.score - a.score || a.it.title.localeCompare(b.it.title));
    return hits.slice(0, 8).map((h) => h.it);
  }, [query, items]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (results.length > 0) {
      navigate(results[0].href);
      setQuery("");
      setOpen(false);
      setMobileOpen(false);
    }
  };

  const onFocus = async () => {
    setOpen(true);
    await ensureIndex();
  };

  // ---- Mobile: icon button that toggles a full-width search row ----
  if (variant === "mobile") {
    return (
      <div ref={wrapRef} className="relative">
        <button
          data-testid="search-toggle-mobile"
          onClick={() => { setMobileOpen((v) => !v); ensureIndex(); }}
          className="p-2 border border-[#222] hover:border-[var(--tc-red)]"
          aria-label="Search"
        >
          {mobileOpen ? <X size={18} /> : <Search size={18} />}
        </button>
        {mobileOpen && (
          <div className="fixed left-0 right-0 top-20 bg-black/95 backdrop-blur-xl border-b border-[#222] p-4 z-40">
            <form onSubmit={onSubmit} className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                ref={inputRef}
                data-testid="search-input-mobile"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={onFocus}
                placeholder="Search t-shirts, hoodies, designs…"
                className="w-full bg-[#0a0a0a] border border-[#222] focus:border-[var(--tc-red)] pl-9 pr-3 py-3 font-body text-sm outline-none"
                autoComplete="off"
              />
            </form>
            <SearchResults
              query={query}
              loading={loading}
              results={results}
              onPick={() => { setMobileOpen(false); setQuery(""); }}
            />
          </div>
        )}
      </div>
    );
  }

  // ---- Desktop: inline input ----
  return (
    <div ref={wrapRef} className="relative w-full">
      <form onSubmit={onSubmit} className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          ref={inputRef}
          data-testid="search-input"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={onFocus}
          placeholder="Search products & designs…"
          className="w-full bg-[#0a0a0a] border border-[#222] focus:border-[var(--tc-red)] pl-8 pr-7 py-2 font-body text-xs outline-none transition-colors"
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            data-testid="search-clear"
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
          >
            <X size={12} />
          </button>
        )}
      </form>
      {open && (query.trim().length >= 2 || loading) && (
        <div className="absolute left-0 right-0 top-full mt-1 surface-card max-h-[80vh] overflow-y-auto z-50 min-w-[340px]">
          <SearchResults query={query} loading={loading} results={results} onPick={() => { setOpen(false); setQuery(""); }} />
        </div>
      )}
    </div>
  );
}

function SearchResults({ query, loading, results, onPick }) {
  const q = query.trim();
  if (loading) {
    return (
      <div className="p-4 flex items-center gap-2 text-zinc-500 font-mono text-xs">
        <Loader2 size={14} className="animate-spin" /> LOADING…
      </div>
    );
  }
  if (q.length < 2) {
    return (
      <div className="p-4 font-mono text-[10px] tracking-widest text-zinc-500">
        TYPE AT LEAST 2 CHARACTERS TO SEARCH
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="p-4">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">NO MATCHES FOR “{q}”</div>
        <Link to="/quote" onClick={onPick} className="font-ui text-xs text-[var(--tc-red)] hover:underline">
          → Tell us what you're looking for, we'll quote it
        </Link>
      </div>
    );
  }
  return (
    <ul data-testid="search-results">
      {results.map((it) => (
        <li key={`${it.kind}-${it.id}`} className="border-b border-[#181818] last:border-b-0">
          <Link
            to={it.href}
            onClick={onPick}
            data-testid={`search-result-${it.id}`}
            className="flex items-center gap-3 p-3 hover:bg-[#141414] transition-colors"
          >
            <div className="w-12 h-12 bg-[#080808] border border-[#222] flex-shrink-0 overflow-hidden">
              {it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" loading="lazy" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-ui font-bold uppercase text-[12px] tracking-wider truncate">{it.title}</div>
              <div className="font-mono text-[9px] text-zinc-500 truncate">
                {it.kind === "product" ? "CUSTOMIZABLE · " : "READY-MADE · "}{it.sub}
              </div>
            </div>
            {typeof it.price === "number" && (
              <div className="font-mono text-[var(--tc-red)] text-xs flex-shrink-0">${it.price.toFixed(2)}</div>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}
